import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Users = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState({
        name: '', last_name: '', identification: '', email: '', city: '', country: '', role: 'user', status: 'active'
    });
    const [isEditing, setIsEditing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [regFields, setRegFields] = useState({
        identification: true, name: true, last_name: true, email: true, city: true, country: true
    });

    useEffect(() => {
        fetchUsers();
        fetchRegFields();
    }, [searchQuery]);

    const fetchRegFields = async () => {
        try {
            const res = await api.get('/configs/registration_fields');
            if (res.data) setRegFields(res.data);
        } catch (error) {
            console.error("Error fetching reg fields", error);
        }
    };

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const response = await api.get(`/users?q=${searchQuery}`);
            if (!searchQuery) {
                console.log("Usuarios cargados (Sin filtros):", response.data);
            }
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await api.put(`/users/${currentUser.id}`, currentUser);
            } else {
                // Registration endpoint usually handles creation
                await api.post('/auth/register', currentUser);
            }
            fetchUsers();
            closeModal();
        } catch (error) {
            console.error("Error saving user", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar este usuario?")) {
            try {
                await api.delete(`/users/${id}`);
                fetchUsers();
            } catch (error) {
                console.error("Error deleting", error);
            }
        }
    };

    const openModal = (user = null) => {
        if (user) {
            setCurrentUser(user);
            setIsEditing(true);
        } else {
            setCurrentUser({
                name: '', last_name: '', identification: '', email: '', city: '', country: '', role: 'user', status: 'active'
            });
            setIsEditing(false);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    return (
        <div className="animate-fadeIn">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Gestión de Usuarios</h1>
                    <p className="text-slate-500 mt-2">Administra los roles y estados de los asistentes y staff.</p>
                </div>
                <div className="flex w-full md:w-auto gap-4">
                    <div className="relative flex-grow">
                        <span className="absolute inset-y-0 left-4 flex items-center text-slate-400">🔍</span>
                        <input 
                            type="text"
                            placeholder="Buscar por nombre, ID o email..."
                            className="bg-white border border-slate-100 pl-12 pr-6 py-3.5 rounded-2xl w-full focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={() => openModal()}
                        className="bg-blue-600 text-white px-8 py-3.5 rounded-2xl font-bold shadow-lg shadow-blue-900/20 hover:bg-blue-500 transition-all flex items-center whitespace-nowrap"
                    >
                        + Nuevo Usuario
                    </button>
                </div>
            </header>

            {isLoading ? (
                <div className="text-center py-20 bg-white rounded-[40px] border border-slate-100">
                    <div className="w-10 h-10 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-400 font-medium">Sincronizando base de datos...</p>
                </div>
            ) : users.length === 0 ? (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 p-20 text-center animate-fadeIn">
                    <div className="text-6xl mb-6 opacity-20">👥</div>
                    <h3 className="text-xl font-bold text-slate-800">No se encontraron usuarios</h3>
                    <p className="text-slate-400 mt-2">Intenta con otro término de búsqueda o registra uno nuevo.</p>
                </div>
            ) : (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Usuario</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Identificación</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Rol / Ciudad</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-center">Estado</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {users.map((u) => (
                                <tr key={u.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="p-6">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-black mr-4 border border-slate-200">
                                                {(u.name?.[0] || u.email?.[0] || '?').toUpperCase()}
                                            </div>
                                            <div className="max-w-[200px] overflow-hidden">
                                                <div className="font-bold text-slate-900 leading-snug truncate">
                                                    {u.name || (u.email ? u.email.split('@')[0] : 'Invitado')} {u.last_name || ''}
                                                </div>
                                                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 truncate">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <div className="text-sm font-semibold text-slate-700">{u.identification || '---'}</div>
                                    </td>
                                    <td className="p-6">
                                        <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
                                            u.role === 'admin' ? 'bg-purple-100 text-purple-600' : 
                                            u.role === 'staff' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                            {u.role}
                                        </span>
                                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">{u.city || 'S/C'}, {u.country || 'S/P'}</div>
                                    </td>
                                    <td className="p-6 text-center">
                                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${u.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className="flex justify-end space-x-2">
                                            <button 
                                                onClick={() => openModal(u)}
                                                className="p-3 rounded-xl bg-slate-100 text-slate-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                            >
                                                ✏️
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(u.id)}
                                                className="p-3 rounded-xl bg-slate-100 text-red-400 hover:bg-red-500 hover:text-white transition-all shadow-sm"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-6 z-[100] animate-fadeIn">
                    <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-3xl overflow-hidden animate-slideUp">
                        <div className="bg-gradient-to-r from-blue-700 to-indigo-600 p-8 text-white relative">
                            <h2 className="text-3xl font-black">{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
                            <p className="text-blue-100 mt-1">Completa los datos del perfil.</p>
                            <button onClick={closeModal} className="absolute top-8 right-8 text-white/50 hover:text-white text-2xl">✕</button>
                        </div>
                        <form onSubmit={handleSave} className="p-10 grid grid-cols-2 gap-6">
                            {/* Nombres */}
                            {regFields.name !== false && (
                                <div className={regFields.last_name === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Nombres</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.name}
                                        onChange={e => setCurrentUser({...currentUser, name: e.target.value})}
                                        required
                                    />
                                </div>
                            )}

                            {/* Apellidos */}
                            {regFields.last_name !== false && (
                                <div className={regFields.name === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Apellidos</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.last_name}
                                        onChange={e => setCurrentUser({...currentUser, last_name: e.target.value})}
                                        required
                                    />
                                </div>
                            )}

                            {/* Email */}
                            <div className={regFields.identification === false ? 'col-span-2' : 'col-span-1'}>
                                <label className="block text-slate-700 text-sm font-bold mb-2">Email</label>
                                <input 
                                    type="email"
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                    value={currentUser.email}
                                    onChange={e => setCurrentUser({...currentUser, email: e.target.value})}
                                    required
                                />
                            </div>

                            {/* Identificación */}
                            {regFields.identification !== false && (
                                <div className="col-span-1">
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Identificación (Cédula)</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.identification}
                                        onChange={e => setCurrentUser({...currentUser, identification: e.target.value})}
                                        required
                                    />
                                </div>
                            )}

                            {/* Ciudad */}
                            {regFields.city !== false && (
                                <div className={regFields.country === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Ciudad</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.city}
                                        onChange={e => setCurrentUser({...currentUser, city: e.target.value})}
                                    />
                                </div>
                            )}

                            {/* País */}
                            {regFields.country !== false && (
                                <div className={regFields.city === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">País</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.country}
                                        onChange={e => setCurrentUser({...currentUser, country: e.target.value})}
                                    />
                                </div>
                            )}

                            {/* Pass/Role/Status - Adaptive */}
                            <div className="col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Rol</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner appearance-none"
                                    value={currentUser.role}
                                    onChange={e => setCurrentUser({...currentUser, role: e.target.value})}
                                >
                                    <option value="user">Usuario</option>
                                    <option value="staff">Staff</option>
                                    <option value="admin">Administrador</option>
                                </select>
                            </div>
                            <div className="col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Estado</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner appearance-none"
                                    value={currentUser.status}
                                    onChange={e => setCurrentUser({...currentUser, status: e.target.value})}
                                >
                                    <option value="active">Activo</option>
                                    <option value="inactive">Inactivo</option>
                                </select>
                            </div>
                            {!isEditing && (
                                <div className="col-span-2">
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Contraseña</label>
                                    <input 
                                        type="password"
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.password || ''}
                                        onChange={e => setCurrentUser({...currentUser, password: e.target.value})}
                                        required
                                        placeholder="Min. 6 caracteres"
                                    />
                                </div>
                            )}
                            <div className="col-span-2 pt-6 flex justify-end space-x-4">
                                <button type="button" onClick={closeModal} className="px-8 py-4 text-slate-500 font-bold hover:text-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="bg-blue-600 text-white px-12 py-4 rounded-3xl font-black shadow-xl shadow-blue-900/20 hover:bg-blue-500 transition-all active:scale-95">
                                    {isEditing ? 'Guardar Cambios' : 'Registrar Usuario'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
